/// <reference types="@types/google.maps" />
import "@googlemaps/js-api-loader";
import type { FC } from "react";

import GoogleMapReact from "google-map-react";

import "./index.css";

import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { PinLayer } from "./pinLayer";

import { Color } from "./color";
import { createInfoWindowContent } from "./infoWindowContent";
import { createPinIcon, createPinLayer } from "./pinLayer";
import { dedupeByCoordinates, validateCoordinates } from "./utils";

type Label = Record<string, string>;

interface Link {
	href: string;
	label: string;
	value: string | number;
}

export interface Marker {
	id: number | string;
	group?: string;
	datetime?: Date;
	lat: number;
	lon?: number;
	lng?: number;
	label: Label[];
	image: string;
	tooltip?: string;
	links?: Link[];
	color?: string;
	scale?: number;
	ordinal: number;
}

export interface Bounds {
	northeast: [number, number];
	southwest: [number, number];
}

export interface LatLng {
	lat: number;
	lng: number;
}

interface MapChartProps {
	id: string;
	data: Marker[];
	type: "map" | "heatmap";
	mapType?: "roadmap" | "satellite";
	height?: number | string;
	loading?: boolean;
	onBoundsChange?: (bounds: Bounds) => void;
	dontUpdateBounds?: boolean;
	forcedCenter?: LatLng;
	lineData?: Marker[];
	showLines?: boolean;
	gestureHandling?: google.maps.MapOptions["gestureHandling"];
}

interface RestoreView {
	bounds: google.maps.LatLngBounds | undefined;
	zoom: number | undefined;
	center: LatLng;
}

export const MapChart: FC<MapChartProps> = ({
	id,
	data = [],
	type,
	mapType,
	height = 372,
	loading,
	onBoundsChange,
	dontUpdateBounds,
	forcedCenter,
	lineData = [],
	showLines = false,
	gestureHandling,
}) => {
	const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
	const [initialized, setInitialized] = useState<boolean>(false);
	const pinLayer = useRef<PinLayer | null>(null);
	const lines = useRef<google.maps.Polyline[]>([]);
	const [isInfoWindowOpen, setIsInfoWindowOpen] = useState<boolean>(false);
	const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
	const infoWindowCloseListener = useRef<google.maps.MapsEventListener | null>(null);
	const restoreViewRef = useRef<RestoreView | null>(null);
	const openMarkerId = useRef<Marker["id"] | null>(null);

	const filteredData = useMemo(() => {
		return dedupeByCoordinates(data);
	}, [data]);

	useEffect(() => {
		if (initialized) return;
		if (filteredData.length) setInitialized(true);
	}, [initialized, filteredData]);

	const center = USA_CENTER;

	const computedBounds = useMemo(() => {
		if (!mapRef) return undefined;
		//If some data is loading, but we've already initialized, don't reset bounds
		if (!filteredData.length || loading) {
			return initialized ? undefined : USA_CENTER_BOUNDS();
		}
		const b = new google.maps.LatLngBounds();
		filteredData.forEach((item) => {
			b.extend({ lat: item.lat, lng: item.lon ?? item.lng! });
		});

		return b;
	}, [filteredData, initialized, loading, mapRef]);

	const onGoogleApiLoaded = useCallback(({ map }: { map: google.maps.Map }) => {
		setMapRef(map);
	}, []);

	const heatmapData = useMemo(() => {
		return {
			positions: filteredData.map((item) => ({
				lat: item.lat,
				lng: item.lon ?? item.lng ?? 0,
				weight: 100,
			})),
			options: {},
		};
	}, [filteredData]);

	useEffect(() => {
		if (!mapRef) return;
		if (!computedBounds) return;
		if (dontUpdateBounds) return;

		mapRef.fitBounds(computedBounds);
	}, [computedBounds, mapRef, dontUpdateBounds]);

	const openInfoWindowRef = useRef(openInfoWindow);
	useEffect(() => {
		openInfoWindowRef.current = openInfoWindow;
	});

	useEffect(() => {
		if (!mapRef) return;
		const layer = createPinLayer(mapRef, (marker, anchorHeight) => openInfoWindowRef.current(marker, anchorHeight));
		pinLayer.current = layer;
		return () => {
			layer.remove();
			pinLayer.current = null;
		};
	}, [mapRef]);

	useEffect(() => {
		pinLayer.current?.setPins(filteredData);

		if (openMarkerId.current === null) return;
		const openMarker = filteredData.find((item) => item.id === openMarkerId.current);
		if (openMarker) infoWindowRef.current?.setPosition(markerPosition(openMarker));
		else closeInfoWindow();
	}, [filteredData, mapRef]);

	function openInfoWindow(item: Marker, anchorHeight: number) {
		if (!mapRef) return;
		const infoWindow = (infoWindowRef.current ??= new google.maps.InfoWindow());
		const position = markerPosition(item);

		restoreViewRef.current ??= { bounds: mapRef.getBounds(), zoom: mapRef.getZoom(), center: position };

		mapRef.setMapTypeId("satellite");
		mapRef.setCenter(position);
		mapRef.setZoom(19);

		infoWindowCloseListener.current?.remove();
		infoWindow.close();
		infoWindow.setContent(createInfoWindowContent(item, () => openStreetView(item)));
		infoWindow.setOptions({ position, pixelOffset: new google.maps.Size(0, -anchorHeight) });
		infoWindow.open({ map: mapRef });
		openMarkerId.current = item.id;
		setIsInfoWindowOpen(true);

		infoWindowCloseListener.current = infoWindow.addListener("close", () => {
			if (!infoWindow.isOpen) restoreView();
		});
	}

	function closeInfoWindow() {
		infoWindowRef.current?.close();
		restoreView();
	}

	function restoreView() {
		const view = restoreViewRef.current;
		restoreViewRef.current = null;
		openMarkerId.current = null;
		setIsInfoWindowOpen(false);
		if (!mapRef || !view) return;

		mapRef.setMapTypeId(mapType ?? "roadmap");
		if (view.bounds) {
			mapRef.fitBounds(view.bounds);
			mapRef.setZoom(view.zoom);
		} else {
			mapRef.setCenter(view.center);
			mapRef.setZoom(11);
		}
	}

	async function openStreetView(item: Marker) {
		if (!mapRef) return;

		const position = markerPosition(item);
		const panorama = mapRef.getStreetView();
		panorama.setPosition(position);
		panorama.setVisible(true);
		mapRef.setStreetView(panorama);

		const icon = await createPinIcon(item, STREET_VIEW_PIN_WIDTH).catch(() => undefined);
		if (!panorama.getVisible()) return;
		const size = new google.maps.Size(STREET_VIEW_PIN_WIDTH, icon?.height ?? STREET_VIEW_PIN_WIDTH);
		const legacyMarker = new google.maps.Marker({
			position,
			title: "",
			icon: { url: icon?.url ?? item.image, size, scaledSize: size },
		});
		legacyMarker.setMap(panorama);

		google.maps.event.addListenerOnce(panorama, "closeclick", () => legacyMarker.setMap(null));
	}

	const viewport = mapRef?.getBounds();

	useEffect(() => {
		const debouncedBoundsChange = debounce((dv) => {
			if (dv != null && onBoundsChange != null) {
				const northEast = dv.getNorthEast();
				const southWest = dv.getSouthWest();

				onBoundsChange({
					northeast: [northEast.lat(), northEast.lng()],
					southwest: [southWest.lat(), southWest.lng()],
				});
			}
		}, 3000);

		if (isInfoWindowOpen) return;
		debouncedBoundsChange(viewport);

		return () => {
			debouncedBoundsChange.cancel();
		};
	}, [viewport, onBoundsChange, isInfoWindowOpen]);

	useEffect(() => {
		if (forcedCenter && mapRef) {
			mapRef.setCenter(forcedCenter);
			mapRef.setZoom(9);
		}
	}, [forcedCenter, mapRef]);

	const clearLines = useCallback(() => {
		lines.current.forEach((lc) => {
			lc.setMap(null);
		});
		lines.current = [];
	}, []);

	useEffect(() => {
		if (!showLines) return;
		if (lineData.length < 2) return;
		if (!mapRef) return;

		// One path per group (rep / machine), drawn in chronological order
		const markersDict = new Map<string, Marker[]>();
		lineData.forEach((marker) => {
			if (!marker.group) return;

			// Skip missing / impossible positions so a path isn't dragged off to 0,0
			const lng = marker.lon ?? marker.lng;
			if (!marker.lat || !lng) return;
			if (!validateCoordinates(marker.lat, lng)) return;

			const group = markersDict.get(marker.group);
			if (group) group.push(marker);
			else markersDict.set(marker.group, [marker]);
		});

		for (const [key, groupMarkers] of markersDict) {
			if (groupMarkers.length < 2) continue;

			const path = [...groupMarkers]
				.sort((a, b) => markerTime(a) - markerTime(b))
				.map((marker) => ({ lat: marker.lat, lng: marker.lon ?? marker.lng! }));

			// Match the pin color so a path is always the same color as the pins it connects
			const color = groupMarkers[0].color ?? Color.fromString(key).toHex();

			const polyline = new google.maps.Polyline({
				path,
				geodesic: true,
				strokeColor: color,
				strokeOpacity: 1,
				strokeWeight: 2,
			});

			polyline.setMap(mapRef);

			lines.current.push(polyline);
		}

		return clearLines;
	}, [lineData, showLines, mapRef, clearLines]);

	return (
		<div id={id} className="relative" style={{ height }}>
			<GoogleMapReact
				bootstrapURLKeys={{
					// TODO: don't hard code this
					key: "AIzaSyDcKVNzjWpSbwSvi8lig6-b5m9iXn7I1zI",
					libraries: ["visualization"],
				}}
				options={{
					minZoom: 3,
					mapTypeId: mapType ?? "roadmap",
					mapTypeControl: true,
					scaleControl: false,
					streetViewControl: false,
					rotateControl: true,
					fullscreenControl: true,
					zoomControl: true,
					clickableIcons: false,
					gestureHandling,
					mapId: "4504f8b37365c3d0",
				}}
				defaultCenter={center}
				defaultZoom={3}
				heatmapLibrary={type === "heatmap"}
				heatmap={type === "heatmap" ? heatmapData : undefined}
				onGoogleApiLoaded={onGoogleApiLoaded}
				yesIWantToUseGoogleMapApiInternals
				shouldUnregisterMapOnUnmount
			/>
			{loading && (
				<div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-white/30">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
				</div>
			)}
		</div>
	);
};

function markerPosition(marker: Marker) {
	return { lat: marker.lat, lng: marker.lon ?? marker.lng! };
}

/** Missing or invalid dates are treated as older than any valid date, matching dedupeByCoordinates. */
function markerTime(marker: Marker) {
	if (!marker.datetime) return 0;
	const time = new Date(marker.datetime).getTime();
	return isNaN(time) ? 0 : time;
}

const STREET_VIEW_PIN_WIDTH = 96;

/** Geographic center of the USA */
const USA_CENTER = { lat: 39.8282, lng: -98.5795 };
const USA_CENTER_BOUNDS = () =>
	google.maps.LatLngBounds &&
	new google.maps.LatLngBounds().extend({ lat: 24.5205, lng: -125.0 }).extend({ lat: 49.3844, lng: -66.9346 });
